/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { StringParam, useQueryParam } from "use-query-params"
import {
  closeSensingSystemRequirementsViewFullRequirementTextDialog,
  closeSensorComponentRequirementsViewFullRequirementTextDialog,
  closeWorldModelSolutionRequirementsViewFullRequirementTextDialog,
  getSensingSystemRequirementsReportFromId,
  getSensorComponentRequirementsReportFromId,
  getWorldModelSolutionRequirementsReportFromId,
  openSensingSystemRequirementsViewFullRequirementTextDialog,
  openSensorComponentRequirementsViewFullRequirementTextDialog,
  openWorldModelSolutionRequirementsViewFullRequirementTextDialog,
  selectSensingSystemRequirementsViewFullRequirementTextDialog,
  selectSensorComponentRequirementsViewFullRequirementTextDialog,
  selectWorldModelSolutionRequirementsViewFullRequirementTextDialog,
  setSensingSystemRequirementsViewFullRequirementTextDialog,
  setSensorComponentRequirementsViewFullRequirementTextDialog,
  setWorldModelSolutionRequirementsViewFullRequirementTextDialog
} from "../../../../../../redux/ducks/AdasPrograms/slice"
import { useAppDispatch, useAppSelector } from "../../../../../../redux/hooks"
import ADAS_PROGRAMS_CONSTANTS from "../../../constants"
import { AdasProgramsReportPageIdx, AdasProgramsReportPages } from "../constants"
import { useMemo } from "react"
import { getPageFromHtabIndex } from "../helpers"
import Text from "../../../../../components/Reports/FullReports/Text"
import ArgosDialog from "../../../../../components/Dialogs/ArgosDialog"
import { ADAS_DIALOG_STYLE } from "../../../../CoverageRequirements/ItemDetails/Tabs/CoverageRequirementsTab/RightPanelComponents/DrivingScenarioProperties"


/**
 * A mapping of ADAS program report pages to their respective dialog state management functions
 * and report retrieval logic for viewing full requirement text dialogs. Each key in the object
 * corresponds to a specific report page, and the value is an object containing the following
 * properties:
 * - `selectDialogState`: A selector function to retrieve the current state of the dialog.
 * - `setDialogState`: An action creator to update the state of the dialog.
 * - `openDialog`: An action creator to open the dialog.
 * - `closeDialog`: An action creator to close the dialog.
 * - `getReportFromId`: A function to retrieve the report data based on a given ID.
 *
 * The available report pages and their corresponding dialog management functions are:
 * - `SENSING_SYSTEM_REQUIREMENT`: Handles dialogs for sensing system requirements.
 * - `SENSOR_COMPONENT_REQUIREMENT`: Handles dialogs for sensor component requirements.
 * - `WORLD_MODEL_SOLUTION_REQUIREMENT`: Handles dialogs for world model solution requirements.
 */
export const viewFullRequirementTextDialogSlice = {
  [AdasProgramsReportPages.SENSING_SYSTEM_REQUIREMENT]: {
    selectDialogState: selectSensingSystemRequirementsViewFullRequirementTextDialog,
    setDialogState: setSensingSystemRequirementsViewFullRequirementTextDialog,
    openDialog: openSensingSystemRequirementsViewFullRequirementTextDialog,
    closeDialog: closeSensingSystemRequirementsViewFullRequirementTextDialog,
    getReportFromId: getSensingSystemRequirementsReportFromId,
  },
  [AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT]: {
    selectDialogState: selectSensorComponentRequirementsViewFullRequirementTextDialog,
    setDialogState: setSensorComponentRequirementsViewFullRequirementTextDialog,
    openDialog: openSensorComponentRequirementsViewFullRequirementTextDialog,
    closeDialog: closeSensorComponentRequirementsViewFullRequirementTextDialog,
    getReportFromId: getSensorComponentRequirementsReportFromId,
  },
  [AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT]: {
    selectDialogState: selectWorldModelSolutionRequirementsViewFullRequirementTextDialog,
    setDialogState: setWorldModelSolutionRequirementsViewFullRequirementTextDialog,
    openDialog: openWorldModelSolutionRequirementsViewFullRequirementTextDialog,
    closeDialog: closeWorldModelSolutionRequirementsViewFullRequirementTextDialog,
    getReportFromId: getWorldModelSolutionRequirementsReportFromId,
  },
}

/**
 * Functional component that renders a dialog displaying the full requirement text
 * of a report.
 *
 * @component
 * @returns {JSX.Element} The rendered dialog component.
 */
export const ViewFullRequirementTextDialog = (): JSX.Element => {
  const dispatch = useAppDispatch()

  const htabid = ADAS_PROGRAMS_CONSTANTS.itemDetails.horizontalTabs.pageHtabs.tabIndexUrlKey
  const [htab] = useQueryParam(htabid, StringParam)
  const page = useMemo(() => getPageFromHtabIndex(htab as AdasProgramsReportPageIdx), [htab])

  const isOpen = useAppSelector(viewFullRequirementTextDialogSlice[page].selectDialogState).isOpen
  const reportId = useAppSelector(viewFullRequirementTextDialogSlice[page].selectDialogState).reportId
  const report = useAppSelector(state => viewFullRequirementTextDialogSlice[page].getReportFromId(state, reportId ?? ''))
  const reportName = report?.reportName ?? null
  const reportRequirement = report?.requirement ?? null
  const onClose = () => {dispatch(viewFullRequirementTextDialogSlice[page].closeDialog())}


  return (
    <ArgosDialog
      {...ADAS_DIALOG_STYLE}
      open={isOpen}
      onClose={onClose}
      headerTitle={`${reportName} Requirement`}
    >
      <Text data={reportRequirement} />
    </ArgosDialog>
  )
}
